import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxDatePipe,
  OnyxDotComponent,
  OnyxIconComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { OrderPointStatus } from '../../../../../../../dashboard/orders/common/enums/order-point-status';
import { CommonHelper } from '../../../../../../helpers/common.helper';
import type { DeadlineType } from '../../../../../../helpers/deadline.helper';
import { DeadlineHelper } from '../../../../../../helpers/deadline.helper';
import { RouteHelper } from '../../../../../../helpers/route.helper';
import { RoutePointValueHelper } from '../../../../../../helpers/route-point-value.helper';
import { DurationPipe } from '../../../../../../pipes/duration.pipe';
import type { RoutePoint } from '../../../../../../services/route.service';
import { RouteService } from '../../../../../../services/route.service';
import { RoutePointTimeWindowsComponent } from '../../../route-point-time-windows/route-point-time-windows.component';

@Component({
  selector: 'app-route-point-details-status-term',
  imports: [
    TranslatePipe,
    OnyxDotComponent,
    OnyxTooltipDirective,
    OnyxDatePipe,
    RoutePointTimeWindowsComponent,
    OnyxIconComponent,
    DurationPipe,
  ],
  templateUrl: './route-point-details-status-term.component.html',
  styleUrl: './route-point-details-status-term.component.scss',
  host: {
    '[class]': `context`,
  },
})
export class RoutePointDetailsStatusTermComponent {
  private readonly routeService = inject(RouteService);
  private readonly commonHelper = inject(CommonHelper);

  public readonly point = input.required<RoutePoint>();
  public readonly type = input.required<DeadlineType>();
  public readonly isCanceled = input.required<boolean>();

  protected readonly I18N = 'route.details.status';
  protected readonly OrderPointStatus = OrderPointStatus;

  protected readonly context = this.routeService.context;

  protected value = computed(() =>
    RoutePointValueHelper.getValue(this.point()),
  );
  protected isArrival = computed(() => this.type() === 'arrival');
  protected timestamps = computed(() =>
    RouteHelper.getTimestamps(this.point()),
  );
  protected estimations = computed(() =>
    RoutePointValueHelper.getEstimations(this.point()),
  );
  protected driversStatuses = computed(() =>
    RouteHelper.getDriverReportedStatuses(this.point()),
  );

  protected isToDo = computed(
    () => RouteHelper.getStatus(this.point()) === OrderPointStatus.TO_DO,
  );
  protected isCompleted = computed(() => RouteHelper.isCompleted(this.point()));
  protected isResolved = computed(() => {
    if (this.isCanceled()) {
      if (!this.isArrival()) return false;
      return !!this.timestamps()?.arrivedAt;
    }

    return this.isArrival() ? !this.isToDo() : this.isCompleted();
  });
  protected isManual = computed(() =>
    this.isArrival()
      ? !this.driversStatuses().has(OrderPointStatus.WAITING)
      : !this.driversStatuses().has(OrderPointStatus.COMPLETED),
  );

  protected completedAt = computed(() =>
    RouteHelper.getCompletedAt(this.point()),
  );
  protected timestamp = computed(() => {
    if (this.isCanceled() && !this.isArrival()) return null;

    return this.isArrival() ? this.timestamps()?.arrivedAt : this.completedAt();
  });
  protected estimatedTime = computed(() =>
    this.isArrival()
      ? this.estimations()?.arrivalTime
      : this.estimations()?.departureTime,
  );

  protected hasPointDeadline = computed(() => {
    if (this.isArrival()) return true;

    const value = this.value();
    return !!(
      RoutePointValueHelper.getTimeWindows(value) ||
      RouteHelper.getBusinessHours(value)
    );
  });
  protected chainDeadline = computed(() => {
    const deadline = DeadlineHelper.getChainDeadline(
      this.point(),
      this.routeService.points(),
    );
    return deadline ? deadline[this.type()] : null;
  });
  protected hasTermConflict = computed(() =>
    RouteHelper.hasChainTermConflict(
      this.point(),
      this.routeService.points(),
      this.type(),
    ),
  );
  protected waitingForServiceDurationWarning = computed(() =>
    this.isArrival()
      ? RouteHelper.getWaitingForServiceDurationWarning(this.point())
      : null,
  );

  protected color = computed(() =>
    RouteHelper.getStatusColor(
      this.point(),
      this.commonHelper.nowMinute(),
      this.type(),
    ),
  );
  protected labels = computed(() =>
    this.isArrival()
      ? {
          label: 'labels.arrival',
          estimationLabel: 'labels.eta',
          unknownLabel: 'labels.unknownEta',
          tooltip: 'labels.estimatedArrivalTime',
        }
      : {
          label: 'labels.departure',
          estimationLabel: 'labels.etd',
          unknownLabel: 'labels.unknownEtd',
          tooltip: 'labels.estimatedDepartureTime',
        },
  );
}
